/**
 * StudyConsentTemplatesPanel - Configuracion > Plantillas > CI de Estudio
 *
 * Lists the study-level Consent Informed templates (separate from the
 * volunteer-level LOPD/Imagenes templates). Provides:
 *   - Install seeds button (idempotent, calls /api/study-consent-templates/seeds/install).
 *   - Filter by category.
 *   - Create / edit / delete / preview PDF.
 *
 * Editor is in a sibling component (StudyConsentTemplateEditor).
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, Pencil, Trash, FileText, DownloadSimple, Lightning } from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { StudyConsentTemplateEditor } from './StudyConsentTemplateEditor';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_LABELS = {
  producto_sanitario: 'Producto Sanitario',
  medicamento: 'Medicamento',
  cosmetico: 'Cosmetico',
  suplemento: 'Suplemento Alimenticio',
};

const CATEGORY_COLORS = {
  producto_sanitario: 'bg-blue-50 text-blue-700 border-blue-200',
  medicamento: 'bg-rose-50 text-rose-700 border-rose-200',
  cosmetico: 'bg-amber-50 text-amber-700 border-amber-200',
  suplemento: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

export function StudyConsentTemplatesPanel({ lang }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [installing, setInstalling] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [creating, setCreating] = useState(false);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API_URL}/api/study-consent-templates?include_inactive=true`,
        { withCredentials: true },
      );
      setTemplates(data || []);
    } catch {
      toast.error(lang === 'es' ? 'Error al cargar plantillas' : 'Error loading templates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTemplates(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleInstallSeeds = async () => {
    setInstalling(true);
    try {
      const { data } = await axios.get(
        `${API_URL}/api/study-consent-templates/seeds/install`,
        { withCredentials: true },
      );
      toast.success(
        lang === 'es'
          ? `${data.installed} plantillas instaladas, ${data.skipped} ya existian`
          : `${data.installed} templates installed, ${data.skipped} skipped`,
      );
      await fetchTemplates();
    } catch {
      toast.error(lang === 'es' ? 'Error instalando plantillas' : 'Error installing templates');
    } finally {
      setInstalling(false);
    }
  };

  const handleDelete = async (t) => {
    const msg = lang === 'es'
      ? `Eliminar la plantilla "${t.name}"?\n\nNo afecta a consentimientos ya firmados con esta plantilla.`
      : `Delete template "${t.name}"?\n\nAlready signed consents are not affected.`;
    if (!window.confirm(msg)) return;
    try {
      await axios.delete(
        `${API_URL}/api/study-consent-templates/${t.template_id}`,
        { withCredentials: true },
      );
      toast.success(lang === 'es' ? 'Plantilla eliminada' : 'Template deleted');
      await fetchTemplates();
    } catch {
      toast.error(lang === 'es' ? 'Error al eliminar' : 'Error deleting');
    }
  };

  const handlePreview = async (t) => {
    try {
      const resp = await axios.post(
        `${API_URL}/api/study-consent-templates/${t.template_id}/preview`,
        {},
        { withCredentials: true, responseType: 'blob' },
      );
      const url = URL.createObjectURL(resp.data);
      window.open(url, '_blank', 'noopener');
    } catch {
      toast.error(lang === 'es' ? 'Error al generar vista previa' : 'Error generating preview');
    }
  };

  const filtered = categoryFilter === 'all'
    ? templates
    : templates.filter(t => t.category === categoryFilter);

  if (editingTemplate || creating) {
    return (
      <StudyConsentTemplateEditor
        lang={lang}
        template={editingTemplate}
        onClose={() => { setEditingTemplate(null); setCreating(false); }}
        onSaved={async () => { await fetchTemplates(); setEditingTemplate(null); setCreating(false); }}
      />
    );
  }

  return (
    <div className="space-y-4" data-testid="study-consent-templates-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-64" data-testid="study-ci-filter-category">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{lang === 'es' ? 'Todas las categorias' : 'All categories'}</SelectItem>
              {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-sm text-stone-500">{filtered.length} {lang === 'es' ? 'plantillas' : 'templates'}</p>
        </div>
        <div className="flex items-center gap-2">
          {templates.length === 0 && (
            <Button
              variant="outline"
              onClick={handleInstallSeeds}
              disabled={installing}
              className="gap-2"
              data-testid="study-ci-install-seeds-btn"
            >
              <Lightning size={16} />
              {installing
                ? (lang === 'es' ? 'Instalando...' : 'Installing...')
                : (lang === 'es' ? 'Instalar plantillas estandar' : 'Install seed templates')}
            </Button>
          )}
          <Button
            onClick={() => setCreating(true)}
            className="gap-2"
            data-testid="study-ci-new-btn"
          >
            <Plus size={16} />
            {lang === 'es' ? 'Nueva plantilla' : 'New template'}
          </Button>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-stone-500">{lang === 'es' ? 'Cargando...' : 'Loading...'}</p>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center space-y-3">
            <FileText size={40} weight="duotone" className="mx-auto text-stone-400" />
            <p className="text-stone-600">
              {templates.length === 0
                ? (lang === 'es' ? 'No hay plantillas. Instala las plantillas estandar para empezar.' : 'No templates. Install the seed templates to get started.')
                : (lang === 'es' ? 'No hay plantillas en esta categoria.' : 'No templates in this category.')}
            </p>
            {templates.length === 0 && (
              <Button onClick={handleInstallSeeds} disabled={installing} className="gap-2">
                <Lightning size={16} />
                {lang === 'es' ? 'Instalar plantillas estandar' : 'Install seed templates'}
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="study-ci-template-grid">
          {filtered.map(t => (
            <Card key={t.template_id} className="hover:border-[#1f3e80] transition-colors">
              <CardContent className="p-4 space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="space-y-1 flex-1 min-w-0">
                    <p className="font-medium text-stone-900 truncate" title={t.name}>{t.name}</p>
                    <p className="text-xs text-stone-500 line-clamp-2">{t.description || (lang === 'es' ? 'Sin descripcion' : 'No description')}</p>
                  </div>
                  <Badge variant="outline" className={`text-xs whitespace-nowrap ${CATEGORY_COLORS[t.category] || ''}`}>
                    {CATEGORY_LABELS[t.category] || t.category}
                  </Badge>
                </div>
                <div className="flex items-center gap-2 text-xs text-stone-500">
                  <span>{t.sections?.length || 0} {lang === 'es' ? 'secciones' : 'sections'}</span>
                  <span>·</span>
                  <span>{t.is_seed ? (lang === 'es' ? 'Plantilla estandar' : 'Seed template') : (lang === 'es' ? 'Personalizada' : 'Custom')}</span>
                  {t.active === false && (
                    <>
                      <span>·</span>
                      <Badge variant="outline" className="text-xs bg-stone-100 text-stone-600">{lang === 'es' ? 'Inactiva' : 'Inactive'}</Badge>
                    </>
                  )}
                </div>
                <div className="flex items-center gap-2 pt-1">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePreview(t)}
                    className="gap-1.5"
                    data-testid={`study-ci-preview-${t.template_id}`}
                  >
                    <DownloadSimple size={14} />
                    {lang === 'es' ? 'Vista previa' : 'Preview'}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setEditingTemplate(t)}
                    className="gap-1.5"
                    data-testid={`study-ci-edit-${t.template_id}`}
                  >
                    <Pencil size={14} />
                    {lang === 'es' ? 'Editar' : 'Edit'}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(t)}
                    className="gap-1.5 text-red-600 hover:text-red-700 hover:bg-red-50 ml-auto"
                    data-testid={`study-ci-delete-${t.template_id}`}
                  >
                    <Trash size={14} />
                    {lang === 'es' ? 'Eliminar' : 'Delete'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
